// Rich-text editor for client-supplied copy (ContentIQ wizard). contentEditable
// with a small execCommand toolbar; the VALUE IS HTML — callers sanitize with
// src/utils/richCopy's sanitizeCopyHtml before saving, and the backend
// re-sanitizes + derives the plain-text twin. Extracted from
// ContentIQSetupWizard.js's local copy (2026-09-29) when the Questions step
// became its second consumer; gained the 🔗 link button then (links survive
// the sanitizer as a[href] http(s)/mailto — the reason the Questions step
// wanted rich text at all: privacy-policy links inline, no separate field).
//
// Props: value (html string), onChange(html), placeholder, minHeight?.
import React from 'react';
import { FONTS, INK, NEUTRAL, RADII, buttonSmall } from '../utils/wizardTheme';
import { htmlToText, pastedHtml } from '../utils/richCopy';

const boxStyle = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: '13px',
  fontFamily: FONTS.body, color: INK.primary, background: NEUTRAL.pageBg,
  border: `1px solid ${NEUTRAL.controlSoft}`, borderRadius: RADII.small,
};

export default function RichCopyEditor({ value, onChange, placeholder, minHeight = '110px' }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && el.innerHTML !== (value || '')) {
      el.innerHTML = value || '';
    }
  }, [value]);
  const cmd = (name, arg) => (e) => {
    e.preventDefault();  // keep the selection in the editor
    document.execCommand(name, false, arg);
    if (ref.current) onChange(ref.current.innerHTML);
  };
  const link = (e) => {
    e.preventDefault();
    // eslint-disable-next-line no-alert
    let url = window.prompt('Link URL (select the text first):', 'https://');
    if (!url) return;
    url = url.trim();
    if (!/^(https?:\/\/|mailto:)/i.test(url)) url = `https://${url}`;
    document.execCommand('createLink', false, url);
    if (ref.current) onChange(ref.current.innerHTML);
  };
  const paste = (e) => {
    // Insert sanitized HTML, never the source's own styles — a Word paste
    // carried fixed widths that squeezed the copy into a narrow column.
    const cd = e.clipboardData;
    if (!cd) return;
    const html = pastedHtml({ html: cd.getData('text/html'), text: cd.getData('text/plain') });
    if (!html) return;
    e.preventDefault();
    document.execCommand('insertHTML', false, html);
    if (ref.current) onChange(ref.current.innerHTML);
  };
  const tbBtn = { ...buttonSmall, padding: '2px 9px', fontSize: '12px' };
  const empty = !htmlToText(value).trim();
  return (
    <div>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
        <button onMouseDown={cmd('bold')} style={{ ...tbBtn, fontWeight: 700 }} title="Bold">B</button>
        <button onMouseDown={cmd('italic')} style={{ ...tbBtn, fontStyle: 'italic' }} title="Italic">I</button>
        <button onMouseDown={cmd('formatBlock', 'h2')} style={tbBtn} title="Heading">H</button>
        <button onMouseDown={cmd('insertUnorderedList')} style={tbBtn} title="Bullet list">•≡</button>
        <button onMouseDown={link} style={tbBtn} title="Link the selected text">🔗</button>
        <span style={{ fontSize: '11px', color: INK.muted, alignSelf: 'center' }}>
          Pasting from Word/Docs keeps bolds, headings &amp; links.
        </span>
      </div>
      <div style={{ position: 'relative' }}>
        {empty && (
          <div style={{ position: 'absolute', top: '8px', left: '10px', fontSize: '13px', color: INK.muted, pointerEvents: 'none' }}>
            {placeholder}
          </div>
        )}
        <div
          ref={ref}
          contentEditable
          role="textbox"
          aria-multiline="true"
          onInput={e => onChange(e.currentTarget.innerHTML)}
          onPaste={paste}
          style={{ ...boxStyle, minHeight, maxHeight: '320px', overflowY: 'auto', lineHeight: 1.5 }}
        />
      </div>
    </div>
  );
}
